<template>
  <div
    role="grid"
    :aria-label="state.title"
    class="grid grid-cols-7 gap-1 p-3 text-center text-xs"
    :class="[$style.root]"
    @keydown="handleKeydown"
  >
    <div
      v-for="weekday in weekdays"
      :key="weekday"
      aria-hidden="true"
      class="py-1 text-muted-foreground"
    >
      {{ weekday }}
    </div>

    <button
      v-for="day in state.days"
      ref="buttons"
      :key="day.key"
      type="button"
      :disabled="day.disabled"
      :tabindex="day.iso === isoCursor ? 0 : -1"
      :data-date="day.iso"
      :aria-label="
        day.iso === isoSelected ? `${day.ariaLabel}, selected` : day.ariaLabel
      "
      :aria-selected="day.iso === isoSelected"
      :class="[
        `size-8 rounded-md border text-sm transition-colors`,
        `outline-2 outline-blue-500 focus-visible:outline`,
        `disabled:pointer-events-none disabled:opacity-30`,
        day.isOutsideMonth && `text-muted-foreground`,
        day.isToday ? `border-muted-foreground` : `border-transparent`,
        day.iso === isoSelected
          ? `bg-blue-500 text-white outline-offset-2`
          : `-outline-offset-1 hover:bg-card`,
      ]"
      @click="emit('select', day.date)"
      @focus="cursor = day.date"
    >
      {{ day.dayOfMonth }}
    </button>
  </div>

  <div class="flex justify-between gap-1 p-3 pt-0">
    <div>
      <button
        type="button"
        class="rounded-full px-2 py-1 text-xs text-muted-foreground outline-2 outline-blue-500 transition hover:text-foreground focus-visible:outline"
        @click="emit('select', null)"
      >
        Clear
      </button>
    </div>

    <div>
      <button
        type="button"
        class="rounded-full px-2 py-1 text-xs text-muted-foreground outline-2 outline-blue-500 transition hover:text-foreground focus-visible:outline"
        @click="emit('select', today)"
      >
        Today
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef } from "vue";

import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  formatFullDate,
  formatISODate,
  formatMonthYear,
  formatWeekdayShort,
  isSameDay,
  startOfMonth,
  startOfWeek,
} from "@/lib/time";

const cursor = defineModel<Date>({ required: true });

const buttons = useTemplateRef("buttons");

const props = defineProps<{
  selected: Date | undefined;
  min: Date | null;
  max: Date | null;
}>();

const emit = defineEmits<{
  select: [date: Date | null];
}>();

const isoCursor = computed(() => {
  return formatISODate(cursor.value);
});

const isoSelected = computed(() => {
  const selected = props.selected;
  return selected && formatISODate(selected);
});

const today = new Date();
const weekdays = computed(() => {
  const base = startOfWeek(today);
  const days: string[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(base);
    date.setDate(base.getDate() + i);
    days.push(formatWeekdayShort(date).slice(0, 2));
  }

  return days;
});

interface GridItem {
  key: string;
  date: Date;
  iso: string;
  dayOfMonth: number;
  isOutsideMonth: boolean;
  isToday: boolean;
  disabled: boolean;
  ariaLabel: string;
}

interface GridState {
  firstOfMonth: Date;
  title: string;
  days: GridItem[];
}

const state = computed<GridState>((prev) => {
  const firstOfMonth = startOfMonth(cursor.value);
  if (prev !== undefined && isSameDay(prev.firstOfMonth, firstOfMonth)) {
    return prev;
  }

  const days: GridItem[] = [];

  const month = firstOfMonth.getMonth();
  const year = firstOfMonth.getFullYear();

  const gridStart = startOfWeek(firstOfMonth);

  const isoToday = formatISODate(today);

  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);

    const isoDate = formatISODate(date);
    const isToday = isoDate === isoToday;

    days.push({
      key: `${year}:${month}:${isoDate}`,
      date: date,
      iso: isoDate,
      dayOfMonth: date.getDate(),
      isOutsideMonth: date.getMonth() !== month,
      isToday: isToday,
      disabled: isDateDisabled(date),
      ariaLabel: formatAriaLabel(date, isToday),
    });
  }

  return {
    firstOfMonth: firstOfMonth,
    title: formatMonthYear(firstOfMonth),
    days: days,
  };
});

const isDateDisabled = (date: Date): boolean => {
  const min = props.min;
  const max = props.max;

  return (min !== null && date < min) || (max !== null && date > max);
};

const formatAriaLabel = (date: Date, isToday: boolean): string => {
  const formatted = formatFullDate(date);
  return isToday ? `Today, ${formatted}` : formatted;
};

const findDayButton = (date: Date): HTMLButtonElement | undefined => {
  const dt = formatISODate(date);
  return buttons.value?.find((btn) => btn.dataset.date === dt);
};

const handleKeydown = (event: KeyboardEvent): void => {
  const current = cursor.value;
  let newDate: Date | null = null;

  switch (event.key) {
    case "Enter":
    case " ": {
      event.preventDefault();

      if (!isDateDisabled(current)) {
        emit("select", current);
      }

      return;
    }
    case "ArrowLeft": {
      event.preventDefault();
      newDate = addDays(current, -1);
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      newDate = addDays(current, 1);
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      newDate = addDays(current, -7);
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      newDate = addDays(current, 7);
      break;
    }
    case "PageUp": {
      event.preventDefault();
      newDate = event.shiftKey ? addYears(current, -1) : addMonths(current, -1);
      break;
    }
    case "PageDown": {
      event.preventDefault();
      newDate = event.shiftKey ? addYears(current, 1) : addMonths(current, 1);
      break;
    }
    case "Home": {
      event.preventDefault();
      newDate = startOfMonth(current);
      break;
    }
    case "End": {
      event.preventDefault();
      newDate = endOfMonth(current);
      break;
    }
  }

  if (newDate && !isDateDisabled(newDate)) {
    cursor.value = newDate;

    nextTick(() => {
      const btn = findDayButton(cursor.value);
      btn?.focus({ preventScroll: true });
    });
  }
};

const focusCursor = (): void => {
  nextTick(() => {
    const btn = findDayButton(cursor.value);
    btn?.focus({ preventScroll: true });
  });
};

defineExpose({ focusCursor });
</script>

<style module>
.root {
  width: 272px;
  height: 264px;
}
</style>
